import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { useCallback } from 'react'
import type { SelectedChildMenuWithDetails } from '~/features/reservationInput/store/childMenu'
import { adultCount$, childMenuAmount$ } from '~/features/reservationInput/store/childMenu'
import { discountCoupons$ } from '~/features/reservationInput/store/discountCoupon'
import {
  disabledGivenPointsUsageSave,
  disabledGivenPointsUsageUseInstantly,
} from '~/features/reservationInput/store/lib/checkDisabled'
import type { SelectedOptionMenuWithDetails } from '~/features/reservationInput/store/optionMenu'
import { selectedOptionMenusWithDetails$ } from '~/features/reservationInput/store/optionMenu'
import { selectedSeat$ } from '~/features/reservationInput/store/seat'
import { LOGIN_SITE, MINIMUM_IKYU_POINT } from '~/lib/constants/constants'
import { isEmpty } from '~/lib/empty'
import { UnknownTypeError } from '~/lib/error/unknownTypeError'
import type {
  DiscountCoupon,
  PaypayUnavailableCause,
  ReservationGivenPointsType,
  ReservationGivenPointsUsage,
  ReserveInfoPoint,
  ReservePaymentType,
} from '~/lib/graphql/graphql'
import type { DiscountCouponCode, HtmlFragment } from '~/lib/graphql/scalars'
import {
  appliedDiscountCoupon$,
  card$,
  discountCouponType$,
  ikyuPointUsage$,
  isAllIkyuPointSelected$,
  isAllPayPayPointSelected$,
  isDiscountCouponEnabled$,
  isIkyuPointEnabled$,
  isPayPayPointEnabled$,
  paymentMethod$,
  paypayPointUsage$,
  useIsAllPayPayPointSelected,
  userSelectedGivenPointsUsage$,
} from './stateMachine/context'
import type {
  ReserveInputCard,
  ReserveInputCards,
  ReserveInputMember,
  ReserveInputPlan,
  ReserveInputRestaurant,
  ReserveInputViewer,
} from './stateMachine/state'
import {
  cards$,
  isMember$,
  isMemberOrPreMember$,
  member$,
  plan$,
  reserveInfo$,
  restaurant$,
  transition$,
  useTransition,
  viewer$,
} from './stateMachine/state'
import type {
  DisplaySeat,
  NewCard,
  SelectedCard,
  SelectedDiscountCouponType,
  UpdatedEvent,
} from './stateMachine/statemachine'

export const givenPoints$ = atom((get) => {
  const rate = get(givenPointsByRate$)
  const fixed = get(givenPointsByFixed$)
  return rate + fixed
})
givenPoints$.debugLabel = 'givenPoints$'

export function useGivenPoints() {
  return useAtomValue(givenPoints$)
}

/** 料率による付与ポイント数 */
const givenPointsByRate$ = atom((get) => {
  const plan = get(plan$)
  const point = get(reserveInfo$)?.point
  const totalAmount = get(totalAmount$)
  const offPriceByCoupon = get(offPriceByCoupon$)
  const ikyuPointUsage = get(appliedIkyuPoints$)
  const chargeAmount = get(selectedSeat$)?.chargeAmount ?? 0
  return calcGivenPointsByRate({
    plan,
    point,
    totalAmount,
    offPriceByCoupon,
    ikyuPointUsage,
    chargeAmount,
  })
})
givenPointsByRate$.debugLabel = 'givenPointsByRate$'

/** 固定ポイントによる付与ポイント数 */
const givenPointsByFixed$ = atom((get) => {
  const plan = get(plan$)
  const point = get(reserveInfo$)?.point
  const adultCount = get(adultCount$)
  return calcGivenPointsByFixed({ plan, point, adultCount })
})
givenPointsByFixed$.debugLabel = 'givenPointsByFixed$'

function calcGivenPointsByRate(data: {
  plan?: ReserveInputPlan
  point?: ReserveInfoPoint
  totalAmount: number
  offPriceByCoupon: number
  ikyuPointUsage: number
  chargeAmount: number
}) {
  if (data.plan === undefined || data.point === undefined) {
    return 0
  }
  const pointRate = data.point.rate / 100

  // 獲得ポイントの算出元となる金額を計算する
  // * PayPay 残高はポイントではないため、計算の対象外となる
  const baseTotalAmount = data.totalAmount - data.offPriceByCoupon - data.ikyuPointUsage

  const baseAmount = data.plan.isSeatOnly
    ? // 席のみの場合
      baseTotalAmount - data.chargeAmount
    : // コースの場合
      baseTotalAmount

  return Math.floor(baseAmount * pointRate)
}

function calcGivenPointsByFixed(data: {
  plan?: ReserveInputPlan
  point?: ReserveInfoPoint
  adultCount: number
}) {
  if (data.plan === undefined || data.point === undefined) {
    return 0
  }

  const pointForCount = data.plan.isSeatOnly
    ? data.point.seatOnlyFixedPoint
    : data.point.courseFixedPoint

  return data.adultCount * pointForCount
}
if (import.meta.vitest) {
  const { test, expect, describe } = import.meta.vitest
  const plan = {
    isSeatOnly: false,
  } as ReserveInputPlan
  const point = {
    rate: 10,
    seatOnlyFixedPoint: 100,
    courseFixedPoint: 0,
  } as ReserveInfoPoint
  const amounts = {
    totalAmount: 5000,
    offPriceByCoupon: 1000,
    ikyuPointUsage: 200,
    chargeAmount: 100,
    adultCount: 2,
  }
  describe('calcGivenPoints', () => {
    test('base case', () => {
      const input = { plan, point, ...amounts }
      expect(calcGivenPointsByRate(input)).toBe(380)
      expect(calcGivenPointsByFixed(input)).toBe(0)
    })
    test('plan と point がない', () => {
      const input = { ...amounts }
      expect(calcGivenPointsByRate(input)).toBe(0)
      expect(calcGivenPointsByFixed(input)).toBe(0)
    })
    test('席のみの場合', () => {
      const input = { plan: { ...plan, isSeatOnly: true }, point, ...amounts }
      expect(calcGivenPointsByRate(input)).toBe(370)
      expect(calcGivenPointsByFixed(input)).toBe(200)
    })
    test('固定ポイントのとき', () => {
      const input = {
        plan,
        point: { ...point, rate: 0, seatOnlyFixedPoint: 50, courseFixedPoint: 100 },
        ...amounts,
      }
      expect(calcGivenPointsByRate(input)).toBe(0)
      expect(calcGivenPointsByFixed(input)).toBe(200)
    })
    test('固定ポイント (席のみ) のとき', () => {
      const input = {
        plan: { ...plan, isSeatOnly: true },
        point: { ...point, rate: 0, seatOnlyFixedPoint: 50, courseFixedPoint: 100 },
        ...amounts,
      }
      expect(calcGivenPointsByRate(input)).toBe(0)
      expect(calcGivenPointsByFixed(input)).toBe(100)
    })
  })
}

const givenPointsByPayment$ = atom((get) => {
  return calcGivenPointsByPayment(get(selectedGivenPointsUsage$), get(givenPoints$))
})

export function calcGivenPointsByPayment(
  selected: ReservationGivenPointsUsage,
  givenPoints: number
) {
  return selected === 'USE_INSTANTLY' ? givenPoints : 0
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('calcGivenPointsByPayment', () => {
    expect(calcGivenPointsByPayment('USE_INSTANTLY', 100)).toBe(100)
    expect(calcGivenPointsByPayment('SAVE', 100)).toBe(0)
  })
}

export const appliedIkyuPoints$ = atom((get) => {
  return getAppliedPoints({
    enabled: get(isIkyuPointEnabled$),
    isAllPointSelected: get(isAllIkyuPointSelected$),
    availableMaxPoint: get(availableMaxIkyuPoint$),
    pointUsage: get(ikyuPointUsage$),
  })
})
appliedIkyuPoints$.debugLabel = 'appliedIkyuPoints$'

export function useAppliedIkyuPoints() {
  return useAtomValue(appliedIkyuPoints$)
}

function getAppliedPoints({
  enabled,
  isAllPointSelected,
  availableMaxPoint,
  pointUsage,
}: {
  enabled: boolean
  isAllPointSelected: boolean
  availableMaxPoint: number
  pointUsage: number
}) {
  if (!enabled) {
    return 0
  }
  if (isAllPointSelected) {
    return availableMaxPoint
  }
  // 利用するポイントは、availableMaxPoint を超えてはいけない
  return Math.min(pointUsage, availableMaxPoint)
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('getAppliedPoints', () => {
    it('ポイント利用が無効の場合は 0', () => {
      expect(
        getAppliedPoints({
          enabled: false,
          isAllPointSelected: false,
          availableMaxPoint: 100,
          pointUsage: 100,
        })
      ).toBe(0)
    })
    it('全ポイントを利用する場合は利用可能なポイント数', () => {
      expect(
        getAppliedPoints({
          enabled: true,
          isAllPointSelected: true,
          availableMaxPoint: 1000,
          pointUsage: 100,
        })
      ).toBe(1000)
    })
    it('全ポイントを利用しない場合は利用ポイント数', () => {
      expect(
        getAppliedPoints({
          enabled: true,
          isAllPointSelected: false,
          availableMaxPoint: 1000,
          pointUsage: 500,
        })
      ).toBe(500)
    })
  })
}

export const appliedPaypayPoints$ = atom((get) => {
  return getAppliedPoints({
    enabled: get(isPayPayPointEnabled$),
    isAllPointSelected: get(isAllPayPayPointSelected$),
    availableMaxPoint: get(availableMaxPayPayBalance$),
    pointUsage: get(paypayPointUsage$),
  })
})
appliedPaypayPoints$.debugLabel = 'appliedPaypayPoints$'

export function useAppliedPaypayPoints() {
  return useAtomValue(appliedPaypayPoints$)
}

/**
 * 支払い金額（予約全体の合計金額からクーポン/ポイントを差し引いた料金）
 */
export const paymentAmount$ = atom((get) => {
  return (
    get(totalAmount$) -
    get(offPriceByCoupon$) -
    get(givenPointsByPayment$) -
    get(appliedPaypayPoints$) -
    get(appliedIkyuPoints$)
  )
})
paymentAmount$.debugLabel = 'paymentAmount$'

export function usePaymentAmount() {
  return useAtomValue(paymentAmount$)
}

/**
 * 予約全体の合計金額（クーポン/ポイントの値引きを含まない料金）
 */
export const totalAmount$ = atom((get) => {
  const inventory = get(reserveInfo$)?.inventory.price ?? 0
  const adultCount = get(adultCount$)
  const chargeAmount = get(selectedSeat$)?.chargeAmount ?? 0
  const childMenus = get(childMenuAmount$)
  const optionMenus = get(selectedOptionMenusWithDetails$) ?? []
  return calcTotalAmount(inventory, adultCount, chargeAmount, childMenus, optionMenus)
})
totalAmount$.debugLabel = 'totalAmount$'

export function calcTotalAmount(
  price: number,
  adultCount: number,
  chargeAmount: number,
  childMenus: SelectedChildMenuWithDetails[],
  optionMenus: SelectedOptionMenuWithDetails[]
) {
  const planAmount = price * adultCount
  const optionMenuAmount = optionMenus.reduce((sum, { price, count }) => sum + price * count, 0)
  const childMenuAmount = childMenus.reduce((sum, { price, count }) => sum + price * count, 0)
  return planAmount + chargeAmount + optionMenuAmount + childMenuAmount
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('calcTotalAmount', () => {
    const childMenus = [
      { price: 1000, count: 1 },
      { price: 2000, count: 2 },
    ] as SelectedChildMenuWithDetails[]
    const optionMenus = [
      { price: 1000, count: 1 },
      { price: 2000, count: 2 },
    ] as SelectedOptionMenuWithDetails[]

    expect(calcTotalAmount(1000, 2, 1000, [], [])).toBe(3000)
    expect(calcTotalAmount(1000, 2, 1000, [], optionMenus)).toBe(8000)
    expect(calcTotalAmount(1000, 2, 0, childMenus, [])).toBe(7000)
    expect(calcTotalAmount(1000, 2, 0, childMenus, optionMenus)).toBe(12000)
  })
}

/**
 * クーポンによって値引きされる金額（総額がクーポンを下回る場合にクーポン額を削る）
 * ※ポイントは考慮しない -> ポイントよりもクーポンの金額を優先して利用すべきなため
 */
export const offPriceByCoupon$ = atom((get) => {
  return getOffPriceByCoupon(
    {
      appliedDiscountCoupon: get(appliedDiscountCoupon$),
      totalAmount: get(totalAmount$),
      discountCoupons: get(discountCoupons$),
      isDiscountCouponEnabled: get(isDiscountCouponEnabled$),
    },
    get(discountCouponType$)
  )
})
offPriceByCoupon$.debugLabel = 'offPriceByCoupon$'

function getOffPriceByCoupon(
  data: {
    appliedDiscountCoupon?: DiscountCoupon
    totalAmount: number
    discountCoupons: DiscountCoupon[]
    isDiscountCouponEnabled: boolean
  },
  couponType?: SelectedDiscountCouponType
) {
  if (couponType === undefined || !data.isDiscountCouponEnabled) {
    return 0
  }
  // クーポンコードを入力して適用した場合は context にセットされている
  // appliedDiscountCoupon$ の値を参照する
  if (couponType.type === 'InputtedCouponCode') {
    return Math.min(data.totalAmount, data.appliedDiscountCoupon?.discountAmount ?? 0)
  }
  // 既に保持しているクーポンを適用した場合は reserveInfo.member から取得した
  // discountCoupons$ の値を参照する
  if (couponType.type === 'AcquiredCoupon') {
    const discountAmount =
      data.discountCoupons.find((coupon) => coupon.code === couponType.code)?.discountAmount ?? 0
    return Math.min(data.totalAmount, discountAmount)
  }
  return 0
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('getOffPriceByCoupon', () => {
    it('獲得済みクーポンを選択', () => {
      expect(
        getOffPriceByCoupon(
          {
            totalAmount: 1000,
            discountCoupons: [{ code: '1234567890', discountAmount: 100 }] as DiscountCoupon[],
            isDiscountCouponEnabled: true,
          },
          { type: 'AcquiredCoupon', code: '1234567890' as DiscountCouponCode }
        )
      ).toBe(100)
    })
    it('入力したクーポンコードを選択', () => {
      expect(
        getOffPriceByCoupon(
          {
            totalAmount: 1000,
            appliedDiscountCoupon: {
              code: '1234567890',
              discountAmount: 200,
            } as DiscountCoupon,
            discountCoupons: [{ code: '1234567890', discountAmount: 100 }] as DiscountCoupon[],
            isDiscountCouponEnabled: true,
          },
          { type: 'InputtedCouponCode' }
        )
      ).toBe(200)
    })
    it('合計金額がクーポン金額を下回っている', () => {
      expect(
        getOffPriceByCoupon(
          {
            totalAmount: 1000,
            discountCoupons: [{ code: '1234567890', discountAmount: 3000 }] as DiscountCoupon[],
            isDiscountCouponEnabled: true,
          },
          { type: 'AcquiredCoupon', code: '1234567890' as DiscountCouponCode }
        )
      ).toBe(1000)
    })
    it('クーポンを使用しない', () => {
      expect(
        getOffPriceByCoupon(
          {
            totalAmount: 1000,
            discountCoupons: [{ code: '1234567890', discountAmount: 100 }] as DiscountCoupon[],
            isDiscountCouponEnabled: false,
          },
          { type: 'AcquiredCoupon', code: '1234567890' as DiscountCouponCode }
        )
      ).toBe(0)
    })
  })
}

export function useOffPriceByCoupon() {
  return useAtomValue(offPriceByCoupon$)
}

/** 選択された獲得ポイントの利用方法 */
const selectedGivenPointsUsage$ = atom((get) => {
  const available = get(availableGivenPointsUsage$)
  const userSelection = get(userSelectedGivenPointsUsage$)
  return selectGivenPointUsage(available, userSelection)
})
selectedGivenPointsUsage$.debugLabel = 'selectedGivenPointsUsage$'

/** 選択された獲得ポイントの利用方法 */
export function useSelectedGivenPointsUsage() {
  return useAtomValue(selectedGivenPointsUsage$)
}

function selectGivenPointUsage(
  available: Set<ReservationGivenPointsUsage>,
  userSelection: ReservationGivenPointsUsage | undefined
): ReservationGivenPointsUsage {
  if (userSelection !== undefined) {
    // ユーザーが選択しているものが選択可能ならそれを返す
    if (available.has(userSelection)) {
      return userSelection
    }
  }
  if (available.size === 0) {
    // 選択可能なものがない場合は SAVE
    return 'SAVE'
  }
  if (available.size === 1) {
    // 選択肢が一つしかない場合はそれ
    return Array.from(available)[0]
  }
  return 'SAVE'
}
if (import.meta.vitest) {
  const { describe, expect, test } = import.meta.vitest
  describe('selectGivenPointUsage', () => {
    test.each`
      available                    | userSelection      | expected
      ${[]}                        | ${undefined}       | ${'SAVE'}
      ${[]}                        | ${'SAVE'}          | ${'SAVE'}
      ${[]}                        | ${'USE_INSTANTLY'} | ${'SAVE'}
      ${['SAVE']}                  | ${undefined}       | ${'SAVE'}
      ${['SAVE']}                  | ${'SAVE'}          | ${'SAVE'}
      ${['SAVE']}                  | ${'USE_INSTANTLY'} | ${'SAVE'}
      ${['USE_INSTANTLY']}         | ${undefined}       | ${'USE_INSTANTLY'}
      ${['USE_INSTANTLY']}         | ${'SAVE'}          | ${'USE_INSTANTLY'}
      ${['USE_INSTANTLY']}         | ${'USE_INSTANTLY'} | ${'USE_INSTANTLY'}
      ${['SAVE', 'USE_INSTANTLY']} | ${undefined}       | ${'SAVE'}
      ${['SAVE', 'USE_INSTANTLY']} | ${'SAVE'}          | ${'SAVE'}
      ${['SAVE', 'USE_INSTANTLY']} | ${'USE_INSTANTLY'} | ${'USE_INSTANTLY'}
    `(
      'returns $expected when available is $available, userSelection is $userSelection',
      (input) => {
        expect(selectGivenPointUsage(new Set(input.available), input.userSelection)).toBe(
          input.expected
        )
      }
    )
  })
}

/** 選択された獲得ポイントの利用方法 (reserve への input 用) */
export const selectedGivenPointsUsageForInput$ = atom((get) => {
  const selected = get(selectedGivenPointsUsage$)
  const givenPoints = get(givenPoints$)
  // 0 ポイントの場合は SAVE にする
  return givenPoints > 0 ? selected : 'SAVE'
})

/** 現在選択できる獲得ポイントの利用方法 */
const availableGivenPointsUsage$ = atom<Set<ReservationGivenPointsUsage>>((get) => {
  const unusable = get(disabledGivenPointsUsageUseInstantly$)
  const unsavable = get(disabledGivenPointsUsageSave$)

  return getAvailableGivenPointsUsage(unusable, unsavable)
})

export function useAvailableGivenPointsUsage() {
  return useAtomValue(availableGivenPointsUsage$)
}

function getAvailableGivenPointsUsage(unusable: boolean, unsavable: boolean) {
  const usages = new Set<ReservationGivenPointsUsage>(['SAVE', 'USE_INSTANTLY'])
  if (unusable) {
    usages.delete('USE_INSTANTLY')
  }
  if (unsavable) {
    usages.delete('SAVE')
  }
  return usages
}

type DisplayGivenPointsType = ReservationGivenPointsUsage | undefined
export const displayGivenPointsType$ = atom((get) => {
  return displayGivenPointsType(
    get(givenPoints$),
    get(isMemberOrPreMember$),
    get(selectedGivenPointsUsage$)
  )
})
displayGivenPointsType$.debugLabel = 'displayGivenPointsType$'

export function useDisplayGivenPointsType() {
  return useAtomValue(displayGivenPointsType$)
}

export function displayGivenPointsType(
  point: number,
  isMemberOrPreMember: boolean,
  usage: ReservationGivenPointsUsage
): DisplayGivenPointsType {
  if (!isMemberOrPreMember) {
    return undefined
  }
  if (point > 0 && usage === 'USE_INSTANTLY') {
    return 'USE_INSTANTLY'
  }
  if (usage === 'SAVE') {
    return 'SAVE'
  }
  return undefined
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('displayGivenPointsType', () => {
    expect(displayGivenPointsType(0, false, 'USE_INSTANTLY')).toBe(undefined)
    expect(displayGivenPointsType(100, false, 'USE_INSTANTLY')).toBe(undefined)
    expect(displayGivenPointsType(100, false, 'SAVE')).toBe(undefined)
    expect(displayGivenPointsType(0, true, 'SAVE')).toBe('SAVE')
    expect(displayGivenPointsType(100, true, 'USE_INSTANTLY')).toBe('USE_INSTANTLY')
    expect(displayGivenPointsType(100, true, 'SAVE')).toBe('SAVE')
  })
}
export const paypayUnavailable$ = atom((get) => {
  return paypayUnavailable(get(member$))
})
paypayUnavailable$.debugLabel = 'paypayUnavailable$'

export function paypayUnavailable(member: ReserveInputMember) {
  if (member === undefined) {
    return undefined
  }
  if (member.paypay.__typename === 'PaypayAvailable') {
    return undefined
  }
  return member.paypay.cause
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it(() => {
    expect(paypayUnavailable(undefined)).toBe(undefined)
    expect(
      paypayUnavailable({
        paypay: {
          __typename: 'PaypayAvailable',
        },
      } as ReserveInputMember)
    ).toBe(undefined)
    expect(
      paypayUnavailable({
        paypay: {
          __typename: 'PaypayUnavailable',
          cause: 'YAHOO_ID_NOT_CONNECTED',
        },
      } as ReserveInputMember)
    ).toBe('YAHOO_ID_NOT_CONNECTED')
  })
}

const disabledGivenPointsUsageUseInstantly$ = atom((get) => {
  return disabledGivenPointsUsageUseInstantly(
    get(isMemberOrPreMember$),
    get(restaurant$),
    get(plan$),
    get(paymentAmountBeforeUseInstantly$),
    get(givenPointsUseInstantly$)
  )
})
disabledGivenPointsUsageUseInstantly$.debugLabel = 'disabledGivenPointsUsageUseInstantly$'

export const disabledGivenPointsUsageSave$ = atom((get) => {
  return disabledGivenPointsUsageSave(get(paypayUnavailable$), get(viewer$))
})
disabledGivenPointsUsageSave$.debugLabel = 'disabledGivenPointsUsageSave$'

export function useDisabledGivenPointsUsageSave() {
  return useAtomValue(disabledGivenPointsUsageSave$)
}

export const canDisplayGivenPointsType$ = atom((get) => {
  return canDisplayGivenPointsType(get(isMember$), get(selectedGivenPointsUsage$), get(viewer$))
})
canDisplayGivenPointsType$.debugLabel = 'canDisplayGivenPointsType$'

export function useCanDisplayGivenPointsType() {
  return useAtomValue(canDisplayGivenPointsType$)
}

export function canDisplayGivenPointsType(
  isMember: boolean,
  usageType: ReservationGivenPointsUsage,
  viewer?: ReserveInputViewer
) {
  // 仕様：https://www.figma.com/file/Xizn2jb2WlVtzaAoZf0maY/Master?type=design&node-id=5632-36628&mode=design&t=ICF289sjTzOXyYj7-0
  // YID ログイン もしくは ゲストで会員登録する場合は獲得ポイントの種類を選ぶことができないので、選択肢を表示しない
  return isMember && viewer?.loginSite !== LOGIN_SITE.Yahoo && usageType === 'SAVE'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('canDisplayGivenPointsType', () => {
    expect(canDisplayGivenPointsType(false, 'USE_INSTANTLY')).toBe(false)
    expect(canDisplayGivenPointsType(true, 'USE_INSTANTLY')).toBe(false)
    expect(
      canDisplayGivenPointsType(true, 'USE_INSTANTLY', {
        loginSite: LOGIN_SITE.Yahoo,
      } as ReserveInputViewer)
    ).toBe(false)
    expect(
      canDisplayGivenPointsType(true, 'USE_INSTANTLY', {
        loginSite: LOGIN_SITE.Ikyu,
      } as ReserveInputViewer)
    ).toBe(false)
    expect(
      canDisplayGivenPointsType(false, 'SAVE', { loginSite: LOGIN_SITE.Ikyu } as ReserveInputViewer)
    ).toBe(false)
    expect(
      canDisplayGivenPointsType(true, 'SAVE', { loginSite: LOGIN_SITE.Ikyu } as ReserveInputViewer)
    ).toBe(true)
  })
}

export const needsYahooConnect$ = atom((get) => {
  return needsYahooConnect(get(viewer$), get(paypayUnavailable$))
})
needsYahooConnect$.debugLabel = 'needsYahooConnect$'

export function useNeedsYahooConnect() {
  return useAtomValue(needsYahooConnect$)
}

export function needsYahooConnect(viewer?: ReserveInputViewer, cause?: PaypayUnavailableCause) {
  return viewer?.loginSite === LOGIN_SITE.Ikyu && cause === 'YAHOO_ID_NOT_CONNECTED'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('needsYahooConnect', () => {
    expect(needsYahooConnect()).toBe(false)
    expect(
      needsYahooConnect(
        { loginSite: LOGIN_SITE.Yahoo } as ReserveInputViewer,
        'YAHOO_ID_NOT_CONNECTED'
      )
    ).toBe(false)
    expect(
      needsYahooConnect(
        { loginSite: LOGIN_SITE.Ikyu } as ReserveInputViewer,
        'INVALID_YAHOO_ID_TOKEN'
      )
    ).toBe(false)
    expect(
      needsYahooConnect(
        { loginSite: LOGIN_SITE.Ikyu } as ReserveInputViewer,
        'YAHOO_ID_NOT_CONNECTED'
      )
    ).toBe(true)
  })
}

export const needsPayPayConnectWithYahooId$ = atom((get) => {
  return needsPayPayConnectWithYahooId(get(paypayUnavailable$), get(viewer$))
})
needsPayPayConnectWithYahooId$.debugLabel = 'needsPayPayConnectWithYahooId$'

export function useNeedsPayPayConnectWithYahooId() {
  return useAtomValue(needsPayPayConnectWithYahooId$)
}

const needsPayPayConnectWithYahooId =
  import.meta.env.VITE_MODE === 'ppg'
    ? ppgNeedsPayPayConnectWithYahooId
    : ikyuNeedsPayPayConnectWithYahooId

function ikyuNeedsPayPayConnectWithYahooId(
  cause?: PaypayUnavailableCause,
  viewer?: ReserveInputViewer
) {
  return viewer?.loginSite === LOGIN_SITE.Yahoo && cause === 'PAYPAY_NOT_CONNECTED'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('ikyuNeedsPayPayConnectWithYahooId', () => {
    expect(ikyuNeedsPayPayConnectWithYahooId()).toBe(false)
    expect(
      ikyuNeedsPayPayConnectWithYahooId('PAYPAY_NOT_CONNECTED', {
        loginSite: LOGIN_SITE.Yahoo,
      } as ReserveInputViewer)
    ).toBe(true)
    expect(
      ikyuNeedsPayPayConnectWithYahooId('INVALID_YAHOO_ID_TOKEN', {
        loginSite: LOGIN_SITE.Ikyu,
      } as ReserveInputViewer)
    ).toBe(false)
    expect(
      ikyuNeedsPayPayConnectWithYahooId('PAYPAY_NOT_CONNECTED', {
        loginSite: LOGIN_SITE.Ikyu,
      } as ReserveInputViewer)
    ).toBe(false)
  })
}

function ppgNeedsPayPayConnectWithYahooId(
  cause?: PaypayUnavailableCause,
  _viewer?: ReserveInputViewer
) {
  return cause === 'PAYPAY_NOT_CONNECTED'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('ppgNeedsPayPayConnectWithYahooId', () => {
    expect(ppgNeedsPayPayConnectWithYahooId()).toBe(false)
    expect(ppgNeedsPayPayConnectWithYahooId('PAYPAY_NOT_CONNECTED')).toBe(true)
    expect(ppgNeedsPayPayConnectWithYahooId('INVALID_YAHOO_ID_TOKEN')).toBe(false)
  })
}

// ./specification.md 「獲得ポイントの初期ポイント種別パターン」参照
export function initialGivenPointsType(data: {
  // viewer と member の違いは ./specification.md 「会員判定について」参照
  viewer?: ReserveInputViewer
  member?: ReserveInputMember
}) {
  if (
    data.viewer?.loginSite === LOGIN_SITE.Yahoo ||
    data.member?.paypay.__typename === 'PaypayAvailable' ||
    (data.member?.paypay.__typename === 'PaypayUnavailable' &&
      (data.member.paypay.cause === 'PAYPAY_NOT_CONNECTED' ||
        data.member.paypay.cause === 'PHONE_NUMBER_UNVERIFIED' ||
        data.member.paypay.cause === 'FAILED_TO_GET_BALANCE'))
  ) {
    return 'PAYPAY'
  }
  return 'IKYU'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('initialGivenPointsType', () => {
    const ikyu = { loginSite: LOGIN_SITE.Ikyu } as ReserveInputViewer
    const yahoo = { loginSite: LOGIN_SITE.Yahoo } as ReserveInputViewer
    const sns = { loginSite: LOGIN_SITE.Line } as ReserveInputViewer
    const paypayBalance = { paypay: { __typename: 'PaypayAvailable' } } as ReserveInputMember
    const yahooIdNotConnected = {
      paypay: { __typename: 'PaypayUnavailable', cause: 'YAHOO_ID_NOT_CONNECTED' },
    } as ReserveInputMember
    const invalidYahooIdToken = {
      paypay: { __typename: 'PaypayUnavailable', cause: 'INVALID_YAHOO_ID_TOKEN' },
    } as ReserveInputMember
    const paypayNotConnected = {
      paypay: { __typename: 'PaypayUnavailable', cause: 'PAYPAY_NOT_CONNECTED' },
    } as ReserveInputMember
    const phoneNumberUnverified = {
      paypay: { __typename: 'PaypayUnavailable', cause: 'PHONE_NUMBER_UNVERIFIED' },
    } as ReserveInputMember
    const failedToGetBalance = {
      paypay: { __typename: 'PaypayUnavailable', cause: 'FAILED_TO_GET_BALANCE' },
    } as ReserveInputMember
    expect(initialGivenPointsType({})).toBe('IKYU')
    expect(initialGivenPointsType({ viewer: ikyu, member: paypayBalance })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: ikyu, member: yahooIdNotConnected })).toBe('IKYU')
    expect(initialGivenPointsType({ viewer: ikyu, member: invalidYahooIdToken })).toBe('IKYU')
    expect(initialGivenPointsType({ viewer: ikyu, member: paypayNotConnected })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: ikyu, member: phoneNumberUnverified })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: ikyu, member: failedToGetBalance })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: yahoo, member: paypayBalance })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: yahoo, member: yahooIdNotConnected })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: yahoo, member: invalidYahooIdToken })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: yahoo, member: paypayNotConnected })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: yahoo, member: phoneNumberUnverified })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: yahoo, member: failedToGetBalance })).toBe('PAYPAY')
    expect(initialGivenPointsType({ viewer: sns, member: yahooIdNotConnected })).toBe('IKYU')
  })
}

export const GivenPointsUserStatus$ = atom((get) => {
  return {
    restaurant: get(restaurant$),
    plan: get(plan$),
    isMember: get(isMember$),
    viewer: get(viewer$),
    givenPoints: get(givenPoints$),
    cause: get(paypayUnavailable$),
  }
})
GivenPointsUserStatus$.debugLabel = 'GivenPointsUserStatus$'

function useGivenPointsUserStatus() {
  return useAtomValue(GivenPointsUserStatus$)
}

export function useChangeGivenPointsUsage() {
  const send = useTransition()
  const userStatus = useGivenPointsUserStatus()
  return useCallback(
    (usage: ReservationGivenPointsUsage) => {
      send({
        type: 'updated',
        givenPointsUsage: usage,
        userStatus,
      })
    },
    [send, userStatus]
  )
}

export function useChangeGivenPointsType() {
  const send = useTransition()
  const userStatus = useGivenPointsUserStatus()
  return useCallback(
    (pointType: ReservationGivenPointsType) => {
      send({
        type: 'updated',
        givenPointsType: pointType,
        userStatus,
      })
    },
    [send, userStatus]
  )
}

/**
 * 利用可能な一休ポイントの最大値
 */
export const availableMaxIkyuPoint$ = atom((get) => {
  const totalAmount = get(totalAmount$)
  const offPriceByCoupon = get(offPriceByCoupon$)
  const usablePoint = get(member$)?.usablePoint ?? 0
  const fixedPoints = get(givenPointsByFixed$)
  const useInstantly = get(selectedGivenPointsUsage$) === 'USE_INSTANTLY'
  return availableMaxIkyuPoint(
    totalAmount,
    offPriceByCoupon,
    usablePoint,
    fixedPoints,
    useInstantly
  )
})
availableMaxIkyuPoint$.debugLabel = 'availableMaxIkyuPoint$'

function availableMaxIkyuPoint(
  totalAmount: number,
  offPriceByCoupon: number,
  usablePoint: number,
  fixedPoints: number,
  useInstantly: boolean
) {
  const totalAfterCoupon = totalAmount - offPriceByCoupon - (useInstantly ? fixedPoints : 0)
  // クーポン適用後の合計金額が、利用できる一休ポイントの最小値を下回る場合は一休ポイントが利用できない
  if (totalAfterCoupon < MINIMUM_IKYU_POINT) {
    return 0
  }
  return Math.min(totalAfterCoupon, usablePoint)
}
if (import.meta.vitest) {
  const { test, expect, describe } = import.meta.vitest
  describe('availableMaxIkyuPoint', () => {
    test('利用可能ポイント無し', () => {
      expect(availableMaxIkyuPoint(1000, 0, 0, 0, false)).toBe(0)
    })
    test('クーポン適用後合計金額(600)が利用可能ポイント(500)を上回る', () => {
      expect(availableMaxIkyuPoint(1000, 400, 500, 0, false)).toBe(500)
    })
    test('クーポン適用後合計金額(10)が利用可能ポイント(500)を下回る', () => {
      expect(availableMaxIkyuPoint(1000, 990, 500, 0, false)).toBe(10)
    })
    test('クーポン適用後合計金額(9)が利用できる一休ポイントの最小値(10)を下回る', () => {
      expect(availableMaxIkyuPoint(1000, 991, 500, 0, false)).toBe(0)
    })
    test('即時利用で固定ポイント数があるときその金額は利用可能ポイント数から除かれる', () => {
      expect(availableMaxIkyuPoint(1000, 0, 1000, 600, true)).toBe(400)
    })
  })
}

const totalIkyuPoint$ = atom((get) => {
  return get(member$)?.totalPoint ?? 0
})
totalIkyuPoint$.debugLabel = 'totalIkyuPoint$'

export function useTotalIkyuPoint() {
  return useAtomValue(totalIkyuPoint$)
}

/**
 * 利用可能なPayPay残高の最大値
 */
export const availableMaxPayPayBalance$ = atom((get) => {
  return calcAvailableMaxPayPayBalance(
    get(totalAmount$),
    get(offPriceByCoupon$),
    get(appliedIkyuPoints$),
    get(givenPointsByPayment$),
    get(paypayBalance$)
  )
})
availableMaxPayPayBalance$.debugLabel = 'availableMaxPayPayBalance$'

function calcAvailableMaxPayPayBalance(
  totalAmount: number,
  appliedIkyuPoints: number,
  offPriceByCoupon: number,
  givenPoints: number,
  paypayBalance: number
) {
  return Math.max(
    Math.min(totalAmount - appliedIkyuPoints - offPriceByCoupon - givenPoints, paypayBalance),
    0
  )
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('calcAvailableMaxPayPayBalance', () => {
    expect(calcAvailableMaxPayPayBalance(4000, 1000, 1000, 1000, 1000)).toBe(1000)
    expect(calcAvailableMaxPayPayBalance(4000, 1000, 1000, 1000, 2000)).toBe(1000)
    expect(calcAvailableMaxPayPayBalance(5000, 1000, 1000, 1000, 2000)).toBe(2000)
    expect(calcAvailableMaxPayPayBalance(4000, 1000, 1000, 1000, 0)).toBe(0)
    expect(calcAvailableMaxPayPayBalance(4000, 5000, 1000, 1000, 2000)).toBe(0)
  })
}

const paypayBalance$ = atom((get) => {
  const member = get(member$)
  if (member?.paypay.__typename === 'PaypayAvailable') {
    return member.paypay.balance
  }
  return 0
})
paypayBalance$.debugLabel = 'paypayBalance$'

export function usePayPayBalance() {
  return useAtomValue(paypayBalance$)
}

export type PayPayMessage =
  | 'needsYahooIdConnection'
  | 'needsPayPayRegistration'
  | 'needsPhoneNumberAuthentication'
  | 'invalidYahooIdToken'
  | 'invalidPayPayBalance'
  | 'hasPaypayMaintenanceMessage'
  | 'paypayUnavailable'
  | 'available'
export const paypayMessage$ = atom((get) => {
  return getPaypayMessage(get(reserveInfo$), get(restaurant$), get(paypayUnavailable$))
})
paypayMessage$.debugLabel = 'paypayMessage$'

export function usePayPayMessage() {
  return useAtomValue(paypayMessage$)
}

export function getPaypayMessage(
  paypayStatusMessages?: { paypayMaintenanceMessage: HtmlFragment },
  restaurant?: ReserveInputRestaurant,
  paypayUnavailable?: PaypayUnavailableCause
): PayPayMessage {
  // paypay 残高利用不可店舗
  // YID 連携有無関わらず利用不可店舗であれば利用できない旨のメッセージを表示する
  if (!restaurant?.paypayAvailable) {
    return 'paypayUnavailable'
  }
  if (paypayStatusMessages !== undefined && paypayStatusMessages.paypayMaintenanceMessage !== '') {
    return 'hasPaypayMaintenanceMessage'
  }
  switch (paypayUnavailable) {
    // PayPay 残高取得成功
    case undefined:
      return 'available'
    // YID 連携していない場合
    case 'YAHOO_ID_NOT_CONNECTED':
      return 'needsYahooIdConnection'
    // YID 連携をしているが、PayPay 連携していない場合
    case 'PAYPAY_NOT_CONNECTED':
      return 'needsPayPayRegistration'
    // YID 連携 / paypay 連携 しているが、電話認証していない場合
    case 'PHONE_NUMBER_UNVERIFIED':
      return 'needsPhoneNumberAuthentication'
    // YID の情報を取得する際に、何らかの理由でエラーが起きた場合
    case 'INVALID_YAHOO_ID_TOKEN':
      return 'invalidYahooIdToken'
    // PayPay 残高を取得する際に、何らかの理由でエラーが起きた場合
    case 'FAILED_TO_GET_BALANCE':
      return 'invalidPayPayBalance'
    default:
      throw new UnknownTypeError(paypayUnavailable)
  }
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('getPaypayMessage', () => {
    it('paypay不可店舗では必ずpaypayUnavailable', () => {
      const message = { paypayMaintenanceMessage: '' } as { paypayMaintenanceMessage: HtmlFragment }
      const paypayUnavailable = { paypayAvailable: false } as ReserveInputRestaurant
      expect(getPaypayMessage(message, paypayUnavailable)).toBe('paypayUnavailable')
      expect(getPaypayMessage(message, paypayUnavailable, 'YAHOO_ID_NOT_CONNECTED')).toBe(
        'paypayUnavailable'
      )
      expect(getPaypayMessage(message, paypayUnavailable, 'PAYPAY_NOT_CONNECTED')).toBe(
        'paypayUnavailable'
      )
      expect(getPaypayMessage(message, paypayUnavailable, 'PHONE_NUMBER_UNVERIFIED')).toBe(
        'paypayUnavailable'
      )
      expect(getPaypayMessage(message, paypayUnavailable, 'INVALID_YAHOO_ID_TOKEN')).toBe(
        'paypayUnavailable'
      )
      expect(getPaypayMessage(message, paypayUnavailable, 'FAILED_TO_GET_BALANCE')).toBe(
        'paypayUnavailable'
      )
    })
    it('paypay利用可能店舗', () => {
      const message = { paypayMaintenanceMessage: '' } as { paypayMaintenanceMessage: HtmlFragment }
      const paypayAvailable = { paypayAvailable: true } as ReserveInputRestaurant
      expect(getPaypayMessage(message, paypayAvailable)).toBe('available')
      expect(getPaypayMessage(message, paypayAvailable, 'YAHOO_ID_NOT_CONNECTED')).toBe(
        'needsYahooIdConnection'
      )
      expect(getPaypayMessage(message, paypayAvailable, 'PAYPAY_NOT_CONNECTED')).toBe(
        'needsPayPayRegistration'
      )
      expect(getPaypayMessage(message, paypayAvailable, 'PHONE_NUMBER_UNVERIFIED')).toBe(
        'needsPhoneNumberAuthentication'
      )
      expect(getPaypayMessage(message, paypayAvailable, 'INVALID_YAHOO_ID_TOKEN')).toBe(
        'invalidYahooIdToken'
      )
      expect(getPaypayMessage(message, paypayAvailable, 'FAILED_TO_GET_BALANCE')).toBe(
        'invalidPayPayBalance'
      )
    })
    it('PayPayメンテナンス中', () => {
      const message = { paypayMaintenanceMessage: 'メンテナンス中' } as {
        paypayMaintenanceMessage: HtmlFragment
      }
      const paypayAvailable = { paypayAvailable: true } as ReserveInputRestaurant
      expect(getPaypayMessage(message, paypayAvailable)).toBe('hasPaypayMaintenanceMessage')
    })
  })
}

export const displayIdentification$ = atom((get) => {
  return displayIdentification(get(paypayMessage$))
})
displayIdentification$.debugLabel = 'displayIdentification$'

export function useDisplayIdentification() {
  return useAtomValue(displayIdentification$)
}

export function displayIdentification(message: PayPayMessage) {
  switch (message) {
    case 'available':
      return '本人確認済'
    case 'needsPhoneNumberAuthentication':
      return '本人確認未'
    case 'invalidYahooIdToken':
      return ''
    case 'needsPayPayRegistration':
      return ''
    case 'needsYahooIdConnection':
      return ''
    case 'invalidPayPayBalance':
      return ''
    case 'paypayUnavailable':
      return ''
    case 'hasPaypayMaintenanceMessage':
      return ''
    default:
      throw new UnknownTypeError(message)
  }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('displayIdentification', () => {
    expect(displayIdentification('needsYahooIdConnection')).toBe('')
    expect(displayIdentification('needsPayPayRegistration')).toBe('')
    expect(displayIdentification('needsPhoneNumberAuthentication')).toBe('本人確認未')
    expect(displayIdentification('invalidYahooIdToken')).toBe('')
    expect(displayIdentification('invalidPayPayBalance')).toBe('')
    expect(displayIdentification('paypayUnavailable')).toBe('')
    expect(displayIdentification('available')).toBe('本人確認済')
  })
}

export function validateStringToNumber(value: string) {
  //数字以外の文字列が入力されると空文字に変換して、数字のみ許容する
  return value.replaceAll(/\D+/g, '')
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('validateStringToNumber', () => {
    expect(validateStringToNumber('123')).toBe('123')
    expect(validateStringToNumber('123a')).toBe('123')
    expect(validateStringToNumber('A1B2C3D')).toBe('123')
    expect(validateStringToNumber('a')).toBe('')
    expect(validateStringToNumber('')).toBe('')
  })
}

const ikyuPointUserStatus$ = atom((get) => {
  return {
    isMember: get(isMember$),
    availableMaxIkyuPoint: get(availableMaxIkyuPoint$),
  }
})
ikyuPointUserStatus$.debugLabel = 'ikyuPointUserStatus$'

export type SelectedCardInfo = NewCard | ReserveInputCard | undefined

export const selectedCardInfo$: Atom<SelectedCardInfo> = atom((get) => {
  return selectedCardInfo(get(cards$), get(card$))
})
selectedCardInfo$.debugLabel = 'selectedCardInfo$'

export function useSelectedCardInfo() {
  return useAtomValue(selectedCardInfo$)
}

function selectedCardInfo(
  myCards: ReserveInputCards,
  selectedCard?: SelectedCard
): NewCard | ReserveInputCard | undefined {
  if (selectedCard === undefined) {
    return undefined
  }
  if (selectedCard.type === 'new') {
    return selectedCard.newCard
  }
  return myCards.find((c) => c.cardId === selectedCard.cardId)
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('selectedCardInfo', () => {
    const newCard = {
      type: 'new',
      newCard: { maskedNumber: '333' },
    } as SelectedCard
    const myCard = { type: 'registered', cardId: '1' } as SelectedCard
    const myCards = [
      { cardId: '0', maskedNumber: '111' },
      { cardId: '1', maskedNumber: '222' },
    ] as ReserveInputCards
    expect(selectedCardInfo(myCards)).toBe(undefined)
    expect(selectedCardInfo(myCards, newCard)).toStrictEqual({ maskedNumber: '333' })
    expect(selectedCardInfo(myCards, myCard)).toStrictEqual(myCards[1])
  })
}

const isRewardsAvailable$ = atom((get) => {
  const restaurant = get(restaurant$)
  const plan = get(plan$)
  return isRewardsAvailable({ restaurant, plan })
})
isRewardsAvailable$.debugLabel = 'isRewardsAvailable$'

function isRewardsAvailable(data: {
  restaurant?: ReserveInputRestaurant
  plan?: ReserveInputPlan
}) {
  if (data.restaurant === undefined || data.plan === undefined) {
    return false
  }
  return data.restaurant.pointAvailable && !data.plan.isSeatOnly
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isRewardsAvailable', () => {
    expect(
      isRewardsAvailable({ restaurant: { pointAvailable: true } as ReserveInputRestaurant })
    ).toBe(false)
    expect(isRewardsAvailable({ plan: { isSeatOnly: false } as ReserveInputPlan })).toBe(false)
    expect(
      isRewardsAvailable({
        restaurant: { pointAvailable: true } as ReserveInputRestaurant,
        plan: { isSeatOnly: false } as ReserveInputPlan,
      })
    ).toBe(true)
  })
}

const isRewardsVisible$ = atom((get) => {
  const pointAndDiscountCouponAvailable = get(isRewardsAvailable$)
  const isMemberOrPreMember = get(isMemberOrPreMember$)
  return isRewardsVisible(pointAndDiscountCouponAvailable, isMemberOrPreMember)
})
isRewardsVisible$.debugLabel = 'isRewardsVisible$'

export function useIsRewardsVisible() {
  return useAtomValue(isRewardsVisible$)
}

function isRewardsVisible(pointAndDiscountCouponAvailable: boolean, isMemberOrPreMember: boolean) {
  return pointAndDiscountCouponAvailable && isMemberOrPreMember
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isRewardsVisible', () => {
    expect(isRewardsVisible(true, true)).toBe(true)
    expect(isRewardsVisible(false, true)).toBe(false)
    expect(isRewardsVisible(true, false)).toBe(false)
    expect(isRewardsVisible(false, false)).toBe(false)
  })
}

const isIkyuPointVisible$ = atom((get) => {
  const rewardsAvailable = get(isRewardsAvailable$)
  const isMember = get(isMember$)
  const viewer = get(viewer$)
  return isIkyuPointVisible(rewardsAvailable, isMember, viewer)
})

export function useIsIkyuPointVisible() {
  return useAtomValue(isIkyuPointVisible$)
}

// ./specification.md 「保有ポイントコンポーネント」参照
// 「ポイント利用可能か」「席のみか」の判定は rewardsAvailable で既に判定している
function isIkyuPointVisible(
  rewardsAvailable: boolean,
  isMember: boolean,
  viewer?: ReserveInputViewer
) {
  if (viewer === undefined) {
    return false
  }
  return rewardsAvailable && isMember && viewer.loginSite !== LOGIN_SITE.Yahoo
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isIkyuPointVisible', () => {
    const ikyu = { loginSite: LOGIN_SITE.Ikyu } as ReserveInputViewer
    const yahoo = { loginSite: LOGIN_SITE.Yahoo } as ReserveInputViewer
    const otherSns = { loginSite: LOGIN_SITE.Line } as ReserveInputViewer
    expect(isIkyuPointVisible(true, true)).toBe(false)
    expect(isIkyuPointVisible(true, true, ikyu)).toBe(true)
    expect(isIkyuPointVisible(true, true, yahoo)).toBe(false)
    expect(isIkyuPointVisible(true, true, otherSns)).toBe(true)
    expect(isIkyuPointVisible(true, false, ikyu)).toBe(false)
    expect(isIkyuPointVisible(true, false, yahoo)).toBe(false)
    expect(isIkyuPointVisible(true, false, otherSns)).toBe(false)
    expect(isIkyuPointVisible(false, true, ikyu)).toBe(false)
    expect(isIkyuPointVisible(false, true, yahoo)).toBe(false)
    expect(isIkyuPointVisible(false, true, otherSns)).toBe(false)
    expect(isIkyuPointVisible(false, false, ikyu)).toBe(false)
    expect(isIkyuPointVisible(false, false, yahoo)).toBe(false)
    expect(isIkyuPointVisible(false, false, otherSns)).toBe(false)
  })
}

const isPayPayVisible$ = atom((get) => {
  const plan = get(plan$)
  const isMember = get(isMember$)
  return isPayPayVisible(isMember, plan)
})

export function useIsPayPayVisible() {
  return useAtomValue(isPayPayVisible$)
}

function isPayPayVisible(isMember: boolean, plan?: ReserveInputPlan) {
  if (plan === undefined) {
    return false
  }
  return !plan.isSeatOnly && isMember
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isPayPayVisible', () => {
    expect(isPayPayVisible(true, { isSeatOnly: true } as ReserveInputPlan)).toBe(false)
    expect(isPayPayVisible(false, { isSeatOnly: true } as ReserveInputPlan)).toBe(false)
    expect(isPayPayVisible(true, { isSeatOnly: false } as ReserveInputPlan)).toBe(true)
    expect(isPayPayVisible(false, { isSeatOnly: false } as ReserveInputPlan)).toBe(false)
  })
}

const requiresOnsitePayment$ = atom((get) => {
  return getRequiresOnsitePayment(get(paymentMethod$), get(plan$))
})
requiresOnsitePayment$.debugLabel = 'requiresOnsitePayment$'

export function useRequiresOnsitePayment() {
  return useAtomValue(requiresOnsitePayment$)
}

function getRequiresOnsitePayment(paymentMethod: ReservePaymentType, plan?: ReserveInputPlan) {
  return paymentMethod === 'ONSITE' && plan?.paymentType !== 'ONLINE_ONLY'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  const onlineOnly = { paymentType: 'ONLINE_ONLY' } as ReserveInputPlan
  const onsiteOnly = { paymentType: 'ONSITE_ONLY' } as ReserveInputPlan
  const both = { paymentType: 'ONSITE_OR_ONLINE' } as ReserveInputPlan
  it('getRequiresOnsitePayment', () => {
    expect(getRequiresOnsitePayment('ONSITE', onlineOnly)).toBe(false)
    expect(getRequiresOnsitePayment('ONSITE', onsiteOnly)).toBe(true)
    expect(getRequiresOnsitePayment('ONSITE', both)).toBe(true)
    expect(getRequiresOnsitePayment('ONSITE')).toBe(true)
    expect(getRequiresOnsitePayment('ONLINE', onlineOnly)).toBe(false)
    expect(getRequiresOnsitePayment('ONLINE', onsiteOnly)).toBe(false)
    expect(getRequiresOnsitePayment('ONLINE', both)).toBe(false)
    expect(getRequiresOnsitePayment('ONLINE')).toBe(false)
  })
}

export function useChangeIkyuPointUsage() {
  const send = useTransition()
  const userStatus = useAtomValue(ikyuPointUserStatus$)
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        ikyuPointUsage: Number(validateStringToNumber(value)),
        userStatus,
      })
    },
    [send, userStatus]
  )
}

const paypayUserStatus$ = atom((get) => {
  return {
    restaurant: get(restaurant$),
    isMember: get(isMember$),
    availableMaxPayPayBalance: get(availableMaxPayPayBalance$),
  }
})
paypayUserStatus$.debugLabel = 'paypayUserStatus$'

export function useChangePayPayPointsUsage() {
  const send = useTransition()
  const userStatus = useAtomValue(paypayUserStatus$)
  return useCallback(
    (value: string) => {
      send({
        type: 'updated',
        paypayPointUsage: Number(validateStringToNumber(value)),
        userStatus,
      })
    },
    [send, userStatus]
  )
}

export function useSetIsAllPayPayPointsSelected() {
  const send = useTransition()
  const paypayPointUsage = useAtomValue(availableMaxPayPayBalance$)
  const userStatus = useAtomValue(paypayUserStatus$)
  return useCallback(
    (v: boolean) => {
      send({
        type: 'updated',
        isAllPayPayPointSelected: v,
        paypayPointUsage,
        userStatus,
      })
    },
    [paypayPointUsage, send, userStatus]
  )
}

export const changeAllIkyuPointUsage$ = atom<null, [boolean], void>(
  null,
  (get, set, useAllIkyuPoint) => {
    const userStatus = get(ikyuPointUserStatus$)
    const event: UpdatedEvent = {
      type: 'updated',
      isAllIkyuPointSelected: useAllIkyuPoint,
      userStatus,
    }
    if (!useAllIkyuPoint) {
      event.ikyuPointUsage = 0
    }
    set(transition$, event)
  }
)

export function useToggleAllPayPayPointsSelected() {
  const checkPayPayPointUsage = useSetIsAllPayPayPointsSelected()
  const checked = useIsAllPayPayPointSelected()
  return useCallback(() => {
    checkPayPayPointUsage(!checked)
  }, [checkPayPayPointUsage, checked])
}

export const changeIkyuPointUsage$ = atom<null, [boolean], void>(null, (get, set, useIkyuPoint) => {
  const userStatus = get(ikyuPointUserStatus$)
  const event: UpdatedEvent = {
    type: 'updated',
    isIkyuPointEnabled: useIkyuPoint,
    userStatus,
  }
  if (useIkyuPoint) {
    event.isAllIkyuPointSelected = true
  }
  set(transition$, event)
})

export function useCheckIsPayPayPointEnabled() {
  const send = useTransition()
  return useCallback(
    (v: boolean) => {
      send({
        type: 'updated',
        isPayPayPointEnabled: v,
      })
    },
    [send]
  )
}

const hiddenBreakDown$ = atom((get) => {
  return hiddenBreakDown({
    plan: get(plan$),
    childMenus: get(childMenuAmount$),
    optionMenus: get(selectedOptionMenusWithDetails$),
    seat: get(selectedSeat$),
  })
})
hiddenBreakDown$.debugLabel = 'hiddenBreakDown$'

function hiddenBreakDown({
  plan,
  childMenus,
  optionMenus,
  seat,
}: {
  plan?: ReserveInputPlan
  childMenus: SelectedChildMenuWithDetails[]
  optionMenus?: SelectedOptionMenuWithDetails[]
  seat?: DisplaySeat
}) {
  const isSeatOnly = plan?.isSeatOnly ?? false
  return isSeatOnly && isEmpty(seat?.chargeAmount) && isEmpty(childMenus) && isEmpty(optionMenus)
}
if (import.meta.vitest) {
  const { it, describe, expect } = import.meta.vitest
  describe('hiddenBreakDown', () => {
    describe('席のみプラン', () => {
      const plan = { isSeatOnly: true } as ReserveInputPlan
      it('チャージ料あり', () => {
        const seat = { chargeAmount: 1000 } as DisplaySeat
        expect(hiddenBreakDown({ plan, childMenus: [], optionMenus: [], seat })).toBe(false)
      })
      it('オプションメニューあり', () => {
        expect(
          hiddenBreakDown({
            plan,
            childMenus: [],
            optionMenus: [{ price: 1000 }] as SelectedOptionMenuWithDetails[],
          })
        ).toBe(false)
      })
      it('子供メニューあり', () => {
        expect(
          hiddenBreakDown({
            plan,
            childMenus: [{ price: 1000 }] as SelectedChildMenuWithDetails[],
            optionMenus: [],
          })
        ).toBe(false)
      })
      it('チャージ料、オプションメニュー、子供メニューなし', () => {
        expect(hiddenBreakDown({ plan, childMenus: [], optionMenus: [] })).toBe(true)
      })
    })
    it('席のみプランではない場合は表示する', () => {
      const plan = { isSeatOnly: false } as ReserveInputPlan
      expect(hiddenBreakDown({ plan, childMenus: [], optionMenus: [] })).toBe(false)
    })
  })
}

export function useHiddenBreakDown() {
  return useAtomValue(hiddenBreakDown$)
}

// -- 獲得ポイント利用を決定するために即時利用時の料金系を計算する必要がある

/** 即時利用適用前の支払い金額 */
const paymentAmountBeforeUseInstantly$ = atom((get) => {
  return (
    get(totalAmount$) -
    get(offPriceByCoupon$) -
    get(availableMaxPayPayBalanceUseInstantly$) -
    get(ownedIkyuPointsToBeUsedUseInstantly$)
  )
})
paymentAmountBeforeUseInstantly$.debugLabel = 'paymentAmountBeforeUseInstantly$'

/** 即時利用したときの付与ポイント数 */
const givenPointsUseInstantly$ = atom((get) => {
  const rate = get(givenPointsByRateUseInstantly$)
  const fixed = get(givenPointsByFixed$)
  return rate + fixed
})
givenPointsUseInstantly$.debugLabel = 'givenPointsUseInstantly$'

/** 即時利用したときの料率による付与ポイント数 */
const givenPointsByRateUseInstantly$ = atom((get) => {
  const plan = get(plan$)
  const point = get(reserveInfo$)?.point
  const totalAmount = get(totalAmount$)
  const offPriceByCoupon = get(offPriceByCoupon$)
  const ikyuPointUsage = get(ownedIkyuPointsToBeUsedUseInstantly$)
  const chargeAmount = get(selectedSeat$)?.chargeAmount ?? 0
  return calcGivenPointsByRate({
    plan,
    point,
    totalAmount,
    offPriceByCoupon,
    ikyuPointUsage,
    chargeAmount,
  })
})
givenPointsByRateUseInstantly$.debugLabel = 'givenPointsByRateUseInstantly$'

/** 即時利用したときの利用保有ポイント数 */
const ownedIkyuPointsToBeUsedUseInstantly$ = atom((get) => {
  return getAppliedPoints({
    enabled: get(isIkyuPointEnabled$),
    isAllPointSelected: get(isAllIkyuPointSelected$),
    availableMaxPoint: get(availableMaxIkyuPointUseInstantly$),
    pointUsage: get(ikyuPointUsage$),
  })
})
ownedIkyuPointsToBeUsedUseInstantly$.debugLabel = 'ownedIkyuPointsToBeUsedUseInstantly$'

/** 即時利用したときの利用可能な一休ポイントの最大値 */
const availableMaxIkyuPointUseInstantly$ = atom((get) => {
  const totalAmount = get(totalAmount$)
  const offPriceByCoupon = get(offPriceByCoupon$)
  const usablePoint = get(member$)?.usablePoint ?? 0
  const fixedPoints = get(givenPointsByFixed$)
  return availableMaxIkyuPoint(totalAmount, offPriceByCoupon, usablePoint, fixedPoints, true)
})
availableMaxIkyuPointUseInstantly$.debugLabel = 'availableMaxIkyuPointUseInstantly$'

/** 即時利用したときの利用可能なPayPay残高の最大値 */
export const availableMaxPayPayBalanceUseInstantly$ = atom((get) => {
  return calcAvailableMaxPayPayBalance(
    get(totalAmount$),
    get(offPriceByCoupon$),
    get(ownedIkyuPointsToBeUsedUseInstantly$),
    get(givenPointsUseInstantly$),
    get(paypayBalance$)
  )
})
availableMaxPayPayBalanceUseInstantly$.debugLabel = 'availableMaxPayPayBalanceUseInstantly$'
